import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaMoneyBillWave,
  FaCreditCard,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaSearch,
} from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { useTranslation } from "react-i18next";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import Pagination from "../../components/Pagination";
import "./AdminPaymentManagement.css";

const PER_PAGE = 10;

const AdminPaymentManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchPayments();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/payments");
      setPayments(response.data || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "#4CAF50",
      pending: "#FF9800",
      failed: "#F44336",
      refunded: "#9E9E9E",
    };
    return colors[status] || "#666";
  };

  const filteredPayments = payments.filter((p) => {
    const matchesStatus =
      filterStatus === "all" || p.paymentStatus === filterStatus;
    const matchesMethod =
      filterMethod === "all" || p.paymentMethod === filterMethod;
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery.trim() ||
      (p._id && p._id.toLowerCase().includes(searchLower)) ||
      (p.order?._id && p.order._id.toLowerCase().includes(searchLower)) ||
      (p.consumer?.name &&
        p.consumer.name.toLowerCase().includes(searchLower)) ||
      (p.consumer?.email &&
        p.consumer.email.toLowerCase().includes(searchLower)) ||
      (p.farmer?.name && p.farmer.name.toLowerCase().includes(searchLower)) ||
      (p.transactionId && p.transactionId.toLowerCase().includes(searchLower));
    return matchesStatus && matchesMethod && matchesSearch;
  });

  const totalPages = Math.ceil(filteredPayments.length / PER_PAGE);
  const pagePayments = filteredPayments.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterStatus, filterMethod]);

  const stats = {
    total: payments.length,
    completed: payments.filter((p) => p.paymentStatus === "completed").length,
    pending: payments.filter((p) => p.paymentStatus === "pending").length,
    failed: payments.filter((p) => p.paymentStatus === "failed").length,
    totalAmount: payments
      .filter((p) => p.paymentStatus === "completed")
      .reduce((sum, p) => sum + (p.amount || 0), 0),
  };

  return (
    <AdminLayout>
      <div className="admin-payment-management-content">
        <div className="admin-payment-management-page-header">
          <div>
            <h1 className="admin-payment-management-page-title">
              {t("adminPaymentManagement.title")}
            </h1>
            <p className="admin-payment-management-page-subtitle">
              {t("adminPaymentManagement.subtitle")}
            </p>
          </div>
        </div>

        <div className="admin-payment-management-stats-cards-grid">
          <div className="admin-payment-management-stat-card">
            <div className="admin-payment-management-stat-icon">
              <FaMoneyBillWave aria-hidden />
            </div>
            <div className="admin-payment-management-stat-content">
              <div className="admin-payment-management-stat-value">
                {toLocalizedNumber(stats.total)}
              </div>
              <div className="admin-payment-management-stat-label">
                {t("adminPaymentManagement.totalPayments")}
              </div>
            </div>
          </div>
          <div className="admin-payment-management-stat-card success">
            <div className="admin-payment-management-stat-icon">
              <FaCheckCircle aria-hidden />
            </div>
            <div className="admin-payment-management-stat-content">
              <div className="admin-payment-management-stat-value">
                {toLocalizedNumber(stats.completed)}
              </div>
              <div className="admin-payment-management-stat-label">
                {t("adminPaymentManagement.completed")}
              </div>
            </div>
          </div>
          <div className="admin-payment-management-stat-card warning">
            <div className="admin-payment-management-stat-icon">
              <FaClock aria-hidden />
            </div>
            <div className="admin-payment-management-stat-content">
              <div className="admin-payment-management-stat-value">
                {toLocalizedNumber(stats.pending)}
              </div>
              <div className="admin-payment-management-stat-label">
                {t("adminPaymentManagement.pending")}
              </div>
            </div>
          </div>
          <div className="admin-payment-management-stat-card danger">
            <div className="admin-payment-management-stat-icon">
              <FaTimesCircle aria-hidden />
            </div>
            <div className="admin-payment-management-stat-content">
              <div className="admin-payment-management-stat-value">
                {toLocalizedNumber(stats.failed)}
              </div>
              <div className="admin-payment-management-stat-label">
                {t("adminPaymentManagement.failed")}
              </div>
            </div>
          </div>
          <div className="admin-payment-management-stat-card highlight">
            <div className="admin-payment-management-stat-icon">
              <FaCreditCard aria-hidden />
            </div>
            <div className="admin-payment-management-stat-content">
              <div className="admin-payment-management-stat-value">
                Rs. {toLocalizedPrice(stats.totalAmount)}
              </div>
              <div className="admin-payment-management-stat-label">
                {t("adminPaymentManagement.totalCollected")}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-payment-management-filters-card">
          <div className="admin-payment-management-search-box">
            <input
              type="text"
              placeholder={t("adminPaymentManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-payment-management-search-input"
            />
            <FaSearch
              className="admin-payment-management-search-icon"
              aria-hidden
            />
          </div>
          <div className="admin-payment-management-filter-tabs">
            <span className="admin-payment-management-filter-label">
              Status:
            </span>
            {["all", "completed", "pending", "failed", "refunded"].map(
              (status) => (
                <button
                  key={status}
                  type="button"
                  className={`admin-payment-management-filter-tab ${filterStatus === status ? "active" : ""}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status === "all" ? "All" : toLocalizedStatus(status, t)} (
                  {toLocalizedNumber(
                    status === "all"
                      ? payments.length
                      : payments.filter((p) => p.paymentStatus === status)
                          .length,
                  )}
                  )
                </button>
              ),
            )}
            <span className="admin-payment-management-filter-label">
              Method:
            </span>
            <button
              type="button"
              className={`admin-payment-management-filter-tab ${filterMethod === "all" ? "active" : ""}`}
              onClick={() => setFilterMethod("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`admin-payment-management-filter-tab ${filterMethod === "COD" ? "active" : ""}`}
              onClick={() => setFilterMethod("COD")}
            >
              COD
            </button>
            <button
              type="button"
              className={`admin-payment-management-filter-tab ${filterMethod === "online" ? "active" : ""}`}
              onClick={() => setFilterMethod("online")}
            >
              Online
            </button>
          </div>
        </div>

        <div className="admin-payment-management-table-card">
          {loading ? (
            <div className="admin-payment-management-loading-state">
              <div className="admin-payment-management-spinner" />
              <p>{t("adminPaymentManagement.loading")}</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="admin-payment-management-empty-state">
              <div className="admin-payment-management-empty-icon">
                <FaMoneyBillWave aria-hidden />
              </div>
              <h3>{t("adminPaymentManagement.noPayments")}</h3>
              <p>{t("adminPaymentManagement.tryAdjusting")}</p>
            </div>
          ) : (
            <div className="admin-payment-management-table-container">
              <table className="admin-payment-management-data-table">
                <thead>
                  <tr>
                    <th>{t("adminPaymentManagement.paymentId")}</th>
                    <th>{t("adminPaymentManagement.order")}</th>
                    <th>{t("adminPaymentManagement.consumer")}</th>
                    <th>{t("adminPaymentManagement.farmer")}</th>
                    <th>{t("adminPaymentManagement.amount")}</th>
                    <th>{t("adminPaymentManagement.method")}</th>
                    <th>{t("adminPaymentManagement.gateway")}</th>
                    <th>{t("adminPaymentManagement.status")}</th>
                    <th>{t("adminPaymentManagement.date")}</th>
                    <th>{t("adminPaymentManagement.paidAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagePayments.map((payment) => (
                    <tr key={payment._id}>
                      <td className="admin-payment-management-id-cell">
                        #{payment._id?.slice(-8) || "N/A"}
                      </td>
                      <td>
                        {payment.order ? (
                          <Link
                            to={`/admin/orders`}
                            className="admin-payment-management-order-link"
                          >
                            #{payment.order._id?.slice(-8) || payment.order}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div className="admin-payment-management-user-info">
                          <div className="admin-payment-management-user-name">
                            {payment.consumer?.name || "N/A"}
                          </div>
                          <div className="admin-payment-management-user-email">
                            {payment.consumer?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="admin-payment-management-user-info">
                          <div className="admin-payment-management-user-name">
                            {payment.farmer?.name || "N/A"}
                          </div>
                          <div className="admin-payment-management-user-email">
                            {payment.farmer?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td className="admin-payment-management-amount">
                        Rs. {toLocalizedPrice(payment.amount || 0)}
                      </td>
                      <td>
                        <span
                          className={`admin-payment-management-method-badge ${payment.paymentMethod === "online" ? "online" : "cod"}`}
                        >
                          {payment.paymentMethod === "COD" ? (
                            <>
                              <FaMoneyBillWave /> COD
                            </>
                          ) : (
                            <>
                              <FaCreditCard /> Online
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        {payment.paymentGateway
                          ? payment.paymentGateway.charAt(0).toUpperCase() +
                            payment.paymentGateway.slice(1)
                          : "—"}
                      </td>
                      <td>
                        <span
                          className="admin-payment-management-status-badge"
                          style={{
                            background: `${getStatusColor(payment.paymentStatus)}22`,
                            color: getStatusColor(payment.paymentStatus),
                          }}
                        >
                          {toLocalizedStatus(
                            payment.paymentStatus || "pending",
                            t,
                          )}
                        </span>
                      </td>
                      <td>
                        {payment.createdAt
                          ? new Date(payment.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        {payment.paidAt
                          ? new Date(payment.paidAt).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentManagement;
